// AP/AR Approval Decision (docs/workflow.md step 6, issue 3 2026-08-11).
// Modeled on ImportControlScreen.tsx: a single-status, click-into-each queue
// with no bulk actions, because AP/AR now carries the review work Import
// Control used to do for every invoice — Import Control narrowed to settling
// an ambiguous company alone (owner ruling 4), so most invoices skip it
// entirely and arrive here still carrying whatever flags Matching raised.
//
// AP/AR is MANDATORY, not present-if-staffed like the three tiers below it: an
// invoice never routes around it, so a company with nobody appointed `ap-ar`
// freezes here — the Pipeline dialog's warning icon on this node exists for
// exactly that.
//
// Company-scoped, exactly like Department/Director/Oversight: the queue is
// whatever the caller's `ap-ar` seat covers, and `canDecide` is the same
// per-row hint those screens use. Two outcomes, no deny (same as Import
// Control): `approved` releases the invoice — the company's approvers gain
// access — and `archived not an invoice` matches a document that turns out not
// to be a payable at all.
//
// AP/AR mounts the SAME shared editor Import Control has always used
// (`EditableInvoiceForm`, `editStep={DecisionStep.ApAr}`) — the full surface,
// unlocked: flags resolvable via Mark resolved, amounts editable, every line's
// expense type editable. The column rule is enforced INSIDE that editor, not
// by a narrowing prop here: it never renders an Account picker and never a
// "Who is it for?" column — the cost center is Department Approval's business
// or later, and the composed account belongs to Export Control and Manual
// Entry. A caller with no `ap-ar` seat covering the invoice's own company
// falls back to the read-only view (no `editStep`), with the disabled
// Approve/Not An Invoice pair and `NOT_AUTHORIZED_REASON` below.
import { useMemo, useState } from 'react'
import { Body1, Button, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { ScreenTitle } from '../components/ScreenTitle'
import type { Invoice } from '../lib/types'
import { DecisionOutcome, DecisionStep, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { createDecision } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, canPreviewPdf, type Session } from '../lib/identity'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
})

interface Props {
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

const NOT_AUTHORIZED_REASON =
  "Only an AP/AR holder assigned to this invoice's own company can decide on it at this step. You can view it, but your decision would be refused."

export function ApArApprovalScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingApAr], refreshNonce)
  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  const decidableIds = useMemo(
    () => new Set(filtered.filter((i) => canDecide(session, DecisionStep.ApAr, i)).map((i) => i.id)),
    [filtered, session],
  )

  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  // One source for one truth: the modal's editable-vs-read-only branch and the
  // row action's disabled state both read this, rather than each computing
  // `canDecide` on the open invoice separately.
  const openDecidable = !!openInvoice && canDecide(session, DecisionStep.ApAr, openInvoice)
  const [archiving, setArchiving] = useState<Invoice | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  function settle() {
    setOpenInvoice(null)
    setArchiving(null)
    void reload()
    onQueuesChanged?.()
  }

  async function approve(invoice: Invoice) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.Approved,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Invoice approved and released to its company's approvers</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function archiveNotAnInvoice(invoice: Invoice, reason: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.ArchivedNotAnInvoice,
        reason,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Archived — Not an Invoice</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>AP/AR Approval</ScreenTitle>
        <Body1 className={s.sub}>For each flagged invoice, resolve all outstanding issues to release for approval.</Body1>
      </div>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading held invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : (
        <InvoiceQueue
          invoices={filtered}
          showFlags
          onOpen={setOpenInvoice}
          rowActions={(inv) => {
            const decidable = decidableIds.has(inv.id)
            const hasUnresolvedFlags = (inv.unresolved_flag_count ?? 0) > 0
            // Server gate 1 is exactly the unresolved-flag count — this withholds
            // nothing the API would accept, it just stops the row's own Approve
            // button offering a click that 409s. The amounts and expense-type
            // gates are not visible from a queue row and stay server refusals
            // surfaced in the ErrorBanner if hit some other way.
            const approveTitle = !decidable
              ? NOT_AUTHORIZED_REASON
              : hasUnresolvedFlags
                ? 'Open the invoice to resolve its flags before approving.'
                : 'Approve'
            return (
              <>
                <Button
                  size="small"
                  className={btn.approve}
                  icon={<CheckmarkRegular />}
                  title={approveTitle}
                  aria-label="Approve"
                  disabled={submitting || !decidable || hasUnresolvedFlags}
                  onClick={() => approve(inv)}
                />
                <Button
                  size="small"
                  className={btn.deny}
                  icon={<DismissRegular />}
                  title={decidable ? 'Archive — not an invoice' : NOT_AUTHORIZED_REASON}
                  aria-label="Archive — not an invoice"
                  disabled={submitting || !decidable}
                  onClick={() => setArchiving(inv)}
                />
              </>
            )
          }}
        />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={canPreviewPdf(session)}
        user={user}
        editStep={openDecidable ? DecisionStep.ApAr : undefined}
        onChanged={() => void reload({ silent: true })}
        onClose={() => setOpenInvoice(null)}
        onReleased={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>Invoice approved and released to its company's approvers</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        onArchived={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>Archived — Not an Invoice</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        actions={
          // Rendered by the modal on its READ-ONLY branch only — a decidable
          // reviewer gets the editable form's own Save & Release / Not An
          // Invoice bar instead (`editStep` above), the same swap Import
          // Control has always had. A non-decidable viewer keeps this
          // disabled pair.
          openInvoice &&
          (() => {
            return (
              <>
                <Button
                  className={btn.approve}
                  icon={<CheckmarkRegular />}
                  disabled={submitting || !openDecidable}
                  title={openDecidable ? undefined : NOT_AUTHORIZED_REASON}
                  onClick={() => approve(openInvoice)}
                >
                  Approve
                </Button>
                <Button
                  className={btn.deny}
                  icon={<DismissRegular />}
                  disabled={submitting || !openDecidable}
                  title={openDecidable ? undefined : NOT_AUTHORIZED_REASON}
                  onClick={() => setArchiving(openInvoice)}
                >
                  Not An Invoice
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!archiving}
        // The heading is the button text that opens this dialog (owner item
        // 5, second sentence, 2026-08-12) — "Not An Invoice" in the modal.
        title="Not An Invoice"
        body="This document will be archived as not an invoice. This comment is recorded on the record and is visible in the decision history."
        requireReason
        reasonLabel="Reason"
        confirmLabel="Archive"
        destructive
        submitting={submitting}
        onCancel={() => setArchiving(null)}
        onConfirm={(reason) => archiving && archiveNotAnInvoice(archiving, reason)}
      />
    </div>
  )
}
